/**
 * Users management page. Renders differently depending on the viewer:
 *  - Superuser / internal: scope tabs (Internal | External), optionally filter
 *    External by merchantId.
 *  - MerchantAdmin: pre-scoped to their own merchant; no scope tabs.
 *
 * Backend already enforces; this UI just hides what's not relevant.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Center,
  Code,
  Collapse,
  Divider,
  Group,
  Loader,
  Menu,
  Modal,
  MultiSelect,
  PasswordInput,
  ScrollArea,
  Select,
  Stack,
  Table,
  Tabs,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import {
  IconAlertCircle,
  IconDotsVertical,
  IconFilter,
  IconFilterOff,
  IconKey,
  IconPlus,
  IconSearch,
  IconShieldCheck,
  IconShieldLock,
  IconShieldOff,
  IconTrash,
  IconUserShield,
} from '@tabler/icons-react';
import { usersApi, type UserRow, type UserType, type PatchUserInput } from '../api/users';
import { rolesApi, type RoleDto } from '../api/roles';
import { merchantsApi } from '../api/merchants';
import { userIpWhitelistApi } from '../api/ipWhitelist';
import { mfaApi } from '../api/mfa';
import { ApiCallError } from '../api/client';
import { useSession, usePermissions } from '../auth/SessionContext';
import { isMerchantUser } from '../auth/permissions';
import { formatGmt7, isIpv4 } from '../lib/format';
import { notifyError } from '../lib/notify';
import { translateStatic } from '../i18n/translations';
import { useTranslation } from '../i18n/LocaleContext';
import { DataTable } from '../components/DataTable';

export function UsersPage() {
  const { t } = useTranslation();
  const { state } = useSession();
  const profile = state.status === 'authenticated' ? state.profile : null;
  const { has, isSuperuser } = usePermissions();
  const canResetMfa = isSuperuser || has('users.mfa.reset');
  const canUnsuspend = isSuperuser || has('users.suspend.reset');
  // Edit is gated by the same permission that's assignable in Roles & Permissions:
  // users.manage (merchant users) / users.internal.manage (internal users).
  // Mirrors the backend authorization in merchant-svc PATCH /api/users/:id.
  const canEdit = (u: UserRow): boolean => {
    if (isSuperuser) return true; // superuser may edit anyone (incl. other superusers)
    if (u.isSuperuser) return false; // non-superuser cannot modify a superuser
    return u.userType === 'internal' ? has('users.internal.manage') : has('users.manage');
  };
  const queryClient = useQueryClient();
  const isMerchant = profile ? isMerchantUser(profile) : false;
  const [scope, setScope] = useState<UserType>(isMerchant ? 'external' : 'internal');
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<UserRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<UserRow | null>(null);
  const [ipTarget, setIpTarget] = useState<UserRow | null>(null);
  const [mfaResetTarget, setMfaResetTarget] = useState<UserRow | null>(null);
  const [unsuspendTarget, setUnsuspendTarget] = useState<UserRow | null>(null);
  // Filter form: draft = pending buffer, applied = what's queried.
  const [draft, setDraft] = useState({ search: '', merchantId: '', roleSlugs: [] as string[] });
  const [applied, setApplied] = useState<{
    search?: string;
    merchantId?: string;
    roleSlugs?: string[];
  }>({});
  const [filtersOpen, setFiltersOpen] = useState(true);

  // Merchant dropdown is only meaningful for internal/superuser viewing the
  // merchant-users scope (internal users have no merchant).
  const showMerchantFilter = !isMerchant && scope === 'external';
  const merchantsForFilter = useQuery({
    queryKey: ['merchants-for-user-filter'],
    queryFn: () => merchantsApi.list({ limit: 100 }),
    enabled: showMerchantFilter,
  });

  // Roles for the multi-select role filter, scoped to the active tab: internal
  // roles on the Internal tab, merchant roles on the External tab. The backend
  // filters by role slug, so a slug from the other scope simply won't match —
  // switching tabs clears the filter (see the Tabs onChange below).
  const roleFilterScope: 'internal' | 'merchant' = scope === 'internal' ? 'internal' : 'merchant';
  const rolesForFilter = useQuery({
    queryKey: ['roles-for-user-filter', roleFilterScope],
    queryFn: () => rolesApi.list({ scope: roleFilterScope }),
    enabled: filtersOpen,
  });

  const usersQuery = useQuery({
    queryKey: ['users', scope, isMerchant ? profile?.merchantId : null, applied],
    queryFn: () =>
      usersApi.list({
        scope,
        ...(isMerchant && profile?.merchantId
          ? { merchantId: profile.merchantId }
          : scope === 'external' && applied.merchantId
            ? { merchantId: applied.merchantId }
            : {}),
        ...(applied.search ? { search: applied.search } : {}),
        ...(applied.roleSlugs && applied.roleSlugs.length > 0
          ? { roleSlugs: applied.roleSlugs }
          : {}),
        limit: 100,
      }),
    enabled: profile != null,
  });

  const activeCount =
    (applied.search ? 1 : 0) +
    (applied.merchantId ? 1 : 0) +
    (applied.roleSlugs?.length ? 1 : 0);

  const applyFilters = () => {
    setApplied({
      ...(draft.search.trim() ? { search: draft.search.trim() } : {}),
      ...(showMerchantFilter && draft.merchantId ? { merchantId: draft.merchantId } : {}),
      ...(draft.roleSlugs.length > 0 ? { roleSlugs: draft.roleSlugs } : {}),
    });
  };

  const clearFilters = () => {
    setDraft({ search: '', merchantId: '', roleSlugs: [] });
    setApplied({});
  };

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <div>
          <Title order={2}>{t('users.title')}</Title>
          <Text c="dimmed" size="sm">
            {isMerchant
              ? t('users.subtitleMerchant')
              : t('users.subtitleInternal')}
          </Text>
        </div>
        <Button leftSection={<IconPlus size={14} />} onClick={() => setCreateOpen(true)}>
          {t('users.newUser')}
        </Button>
      </Group>

      {!isMerchant && (
        <Tabs
          value={scope}
          onChange={(v) => {
            setScope((v as UserType) ?? 'internal');
            clearFilters(); // role slugs are scope-specific; reset on tab switch
          }}
        >
          <Tabs.List>
            <Tabs.Tab value="internal">{t('users.tabInternal')}</Tabs.Tab>
            <Tabs.Tab value="external">{t('users.tabExternal')}</Tabs.Tab>
          </Tabs.List>
        </Tabs>
      )}

      <Card withBorder p="md">
        <Group justify="space-between" mb={filtersOpen ? 'sm' : 0}>
          <Group gap="xs">
            <Text fw={600} size="sm">{t('common.filters')}</Text>
            {activeCount > 0 && (
              <Badge size="sm" variant="light">{t('users.activeFilters', { n: activeCount })}</Badge>
            )}
          </Group>
          <Button
            variant="subtle"
            size="xs"
            onClick={() => setFiltersOpen((o) => !o)}
            leftSection={filtersOpen ? <IconFilterOff size={14} /> : <IconFilter size={14} />}
          >
            {filtersOpen ? t('common.collapse') : t('common.show')}
          </Button>
        </Group>
        <Collapse in={filtersOpen}>
          <Group align="end" wrap="wrap" gap="sm">
            <TextInput
              label={t('users.filterSearch')}
              placeholder={t('users.filterSearchPh')}
              leftSection={<IconSearch size={14} />}
              value={draft.search}
              onChange={(e) => setDraft((d) => ({ ...d, search: e.currentTarget.value }))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') applyFilters();
              }}
              style={{ flex: 1, minWidth: 220 }}
            />
            {showMerchantFilter && (
              <Select
                label={t('users.filterMerchant')}
                placeholder={t('merchants.filterAll')}
                clearable
                searchable
                data={(merchantsForFilter.data?.rows ?? []).map((m) => ({
                  value: m.id,
                  label: m.name,
                }))}
                value={draft.merchantId || null}
                onChange={(v) => setDraft((d) => ({ ...d, merchantId: v ?? '' }))}
                w={240}
              />
            )}
            <MultiSelect
              label={t('users.filterRole')}
              placeholder={draft.roleSlugs.length === 0 ? t('users.filterRolePh') : undefined}
              clearable
              searchable
              hidePickedOptions
              data={(rolesForFilter.data?.rows ?? []).map((r) => ({
                value: r.slug,
                label: r.name,
              }))}
              value={draft.roleSlugs}
              onChange={(v) => setDraft((d) => ({ ...d, roleSlugs: v }))}
              w={280}
            />
            <Button variant="default" onClick={clearFilters}>
              {t('common.reset')}
            </Button>
            <Button onClick={applyFilters}>{t('common.apply')}</Button>
          </Group>
        </Collapse>
      </Card>

      <Card p={0}>
        {usersQuery.isLoading && (
          <Center py="xl"><Loader /></Center>
        )}
        {usersQuery.error && (
          <Alert color="red" icon={<IconAlertCircle size={16} />} m="md">
            {usersQuery.error instanceof ApiCallError
              ? `${usersQuery.error.code}: ${usersQuery.error.message}`
              : t('common.error')}
          </Alert>
        )}
        {!usersQuery.isLoading && !usersQuery.error && (
          <DataTable
            tableId={scope === 'external' && !isMerchant ? 'users-external' : `users-${scope}`}
            columns={[
              { key: 'email', label: t('users.colEmail'), width: 320 },
              { key: 'name', label: t('users.colName'), width: 200 },
              { key: 'role', label: t('users.colRole'), width: 170 },
              ...(scope === 'external' && !isMerchant
                ? [{ key: 'merchant', label: t('users.colMerchant'), width: 200 }]
                : []),
              { key: 'lastLogin', label: t('users.colLastLogin'), width: 170 },
              { key: 'created', label: t('users.colCreated'), width: 170 },
              { key: 'action', width: 56, fixed: true, sticky: 'right' },
            ]}
          >
                {(usersQuery.data?.rows ?? []).length === 0 && (
                  <Table.Tr><Table.Td colSpan={7}>
                    <Text c="dimmed" ta="center" py="lg">{t('users.empty')}</Text>
                  </Table.Td></Table.Tr>
                )}
                {(usersQuery.data?.rows ?? []).map((u) => (
                  <Table.Tr key={u.id}>
                    <Table.Td>
                      <Group gap={6}>
                        <Text size="sm">{u.email}</Text>
                        {u.isSuperuser && (
                          <Badge size="xs" color="red" variant="light" leftSection={<IconUserShield size={10} />}>
                            {t('users.badgeSuperuser')}
                          </Badge>
                        )}
                        {u.suspendedAt && (
                          <Tooltip
                            label={t('users.suspendDetail', {
                              date: formatGmt7(u.suspendedAt),
                              reason: userSuspendReasonLabel(t, u.suspendedReason),
                            })}
                            withArrow
                          >
                            <Badge size="xs" color="red" variant="filled">
                              {t('users.suspendedBadge')}
                            </Badge>
                          </Tooltip>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td>{u.accountName}</Table.Td>
                    <Table.Td>
                      {u.roleName ? (
                        <div>
                          <Text size="sm">{u.roleName}</Text>
                          <Text size="xs" c="dimmed" ff="monospace">{u.roleSlug}</Text>
                        </div>
                      ) : (
                        <Text size="sm" c="dimmed">{t('users.noRole')}</Text>
                      )}
                    </Table.Td>
                    {scope === 'external' && !isMerchant && (
                      <Table.Td>
                        {u.merchantName ?? (
                          <Text size="xs" c="dimmed" ff="monospace">
                            {u.merchantId?.slice(0, 8)}…
                          </Text>
                        )}
                      </Table.Td>
                    )}
                    <Table.Td>
                      <Text size="sm" c="dimmed">
                        {u.lastLoginAt ? formatGmt7(u.lastLoginAt) : '—'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">{formatGmt7(u.createdAt)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Menu shadow="md" position="bottom-end">
                        <Menu.Target>
                          {/* Superuser row stays partly clickable: only delete
                              is forbidden, so we render the action menu but
                              the destructive item is hidden below. */}
                          <ActionIcon variant="subtle">
                            <IconDotsVertical size={16} />
                          </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                          <Menu.Item
                            leftSection={<IconShieldLock size={14} />}
                            onClick={() => setIpTarget(u)}
                          >
                            {t('users.menuIpWhitelist')}
                          </Menu.Item>
                          <Menu.Item
                            leftSection={<IconKey size={14} />}
                            disabled={!canEdit(u)}
                            onClick={() => {
                              // Button stays visible/clickable, but a user may not
                              // edit their own account here — show a clear alert.
                              if (u.id === profile?.id) {
                                notifyError(
                                  translateStatic('notify.user.selfEditMsg'),
                                  translateStatic('notify.user.selfEditTitle'),
                                );
                                return;
                              }
                              setEditTarget(u);
                            }}
                          >
                            {t('users.menuEditUser')}
                          </Menu.Item>
                          {canResetMfa && (
                            <Menu.Item
                              leftSection={<IconShieldOff size={14} />}
                              disabled={!u.mfaEnabled}
                              onClick={() => setMfaResetTarget(u)}
                            >
                              {u.mfaEnabled ? t('users.menuResetMfa') : t('users.menuMfaInactive')}
                            </Menu.Item>
                          )}
                          {canUnsuspend && u.suspendedAt && (
                            <Menu.Item
                              leftSection={<IconShieldCheck size={14} />}
                              onClick={() => setUnsuspendTarget(u)}
                              color="green"
                            >
                              {t('users.menuUnsuspend')}
                            </Menu.Item>
                          )}
                          <Menu.Divider />
                          <Menu.Item
                            color="red"
                            leftSection={<IconTrash size={14} />}
                            disabled={u.isSuperuser}
                            onClick={() => setDeleteTarget(u)}
                          >
                            {t('common.delete')}
                          </Menu.Item>
                        </Menu.Dropdown>
                      </Menu>
                    </Table.Td>
                  </Table.Tr>
                ))}
          </DataTable>
        )}
      </Card>

      <CreateUserModal
        opened={createOpen}
        scope={scope}
        merchantId={isMerchant ? profile?.merchantId ?? null : null}
        onClose={() => setCreateOpen(false)}
        onCreated={() => {
          setCreateOpen(false);
          queryClient.invalidateQueries({ queryKey: ['users'] });
        }}
      />

      {editTarget && (
        <EditUserModal
          user={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={() => {
            setEditTarget(null);
            queryClient.invalidateQueries({ queryKey: ['users'] });
          }}
        />
      )}

      {deleteTarget && (
        <DeleteUserModal
          user={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={() => {
            setDeleteTarget(null);
            queryClient.invalidateQueries({ queryKey: ['users'] });
          }}
        />
      )}

      {ipTarget && (
        <IpWhitelistModal user={ipTarget} onClose={() => setIpTarget(null)} />
      )}

      {mfaResetTarget && (
        <MfaResetModal
          user={mfaResetTarget}
          onClose={() => setMfaResetTarget(null)}
          onDone={() => {
            setMfaResetTarget(null);
            queryClient.invalidateQueries({ queryKey: ['users'] });
          }}
        />
      )}

      {unsuspendTarget && (
        <UnsuspendUserModal
          user={unsuspendTarget}
          onClose={() => setUnsuspendTarget(null)}
          onDone={() => {
            setUnsuspendTarget(null);
            queryClient.invalidateQueries({ queryKey: ['users'] });
          }}
        />
      )}
    </Stack>
  );
}

// Translate the suspend reason enum into a localised label. Returns '—' for
// unknown reasons so the UI never breaks when a future migration introduces a
// new value.
function userSuspendReasonLabel(
  t: ReturnType<typeof useTranslation>['t'],
  reason: string | null | undefined,
): string {
  if (!reason) return '—';
  if (reason === 'too_many_failed_logins') return t('users.suspendReason.too_many_failed_logins');
  if (reason === 'merchant_suspended_cascade') return t('users.suspendReason.merchant_suspended_cascade');
  if (reason === 'manual') return t('users.suspendReason.manual');
  return reason;
}

/**
 * Clears a user's suspended state and resets their failed-login counter.
 * Permission-gated by `users.suspend.reset` (only internal_admin + superuser by
 * default). Audit-logged on the server.
 */
function UnsuspendUserModal({
  user,
  onClose,
  onDone,
}: {
  user: UserRow;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const mut = useMutation({
    meta: { successKey: 'notify.user.unsuspend.ok', errorKey: 'notify.user.unsuspend.fail' },
    mutationFn: () => usersApi.unsuspend(user.id),
    onSuccess: () => onDone(),
    onError: (e) => {
      if (e instanceof ApiCallError) notifyError(e.message, e.code);
      else notifyError((e as Error).message);
    },
  });
  return (
    <Modal opened onClose={onClose} title={t('users.unsuspendTitle', { email: user.email })} centered>
      <Stack>
        <Alert color="orange" icon={<IconAlertCircle size={16} />}>
          {t('users.unsuspendWarning', { email: user.email })}
        </Alert>
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={mut.isPending}>
            {t('common.cancel')}
          </Button>
          <Button color="green" onClick={() => mut.mutate()} loading={mut.isPending}>
            {t('users.unsuspendConfirm')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/**
 * Admin disable of another user's MFA. Used when the user loses their
 * authenticator device. After reset, the user must re-enroll via Security
 * page before doing disbursement again. Audit-logged.
 */
function MfaResetModal({
  user,
  onClose,
  onDone,
}: {
  user: UserRow;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const mut = useMutation({
    meta: { successKey: 'notify.mfa.reset.ok', errorKey: 'notify.mfa.reset.fail' },
    mutationFn: () => mfaApi.adminReset(user.id),
    onSuccess: () => onDone(),
  });
  return (
    <Modal opened={true} onClose={onClose} title={t('users.resetMfaTitle', { email: user.email })} size="md">
      <Stack gap="md">
        <Alert color="yellow" icon={<IconAlertCircle size={16} />} variant="light">
          <Text size="sm">
            {t('users.resetMfaWarning', { email: user.email })}
          </Text>
        </Alert>
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={mut.isPending}>
            {t('common.cancel')}
          </Button>
          <Button color="red" onClick={() => mut.mutate()} loading={mut.isPending}>
            {t('users.resetMfaConfirm')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/**
 * Per-user IP whitelist editor. Replaces the standalone "My IP Whitelist"
 * page — admins manage other users' whitelists from here, and any user
 * managing their own row (they appear in the list themselves) edits their
 * own whitelist with the same UI.
 *
 * Backend authorization: self-management is allowed; admin permissions are
 * checked server-side based on the caller's role.
 */
function IpWhitelistModal({
  user,
  onClose,
}: {
  user: UserRow;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const listQuery = useQuery({
    queryKey: ['user-ip-whitelist', user.id],
    queryFn: () => userIpWhitelistApi.list(user.id),
  });

  const form = useForm({
    initialValues: { cidr: '', label: '' },
    validate: {
      cidr: (v) =>
        v.trim().length === 0
          ? t('users.ipWhitelistRequired')
          : isIpv4(v)
            ? null
            : t('users.ipWhitelistInvalidIp'),
      label: (v) => (v.trim().length === 0 ? t('users.ipWhitelistRequired') : null),
    },
  });

  const addMut = useMutation({
    meta: { successKey: 'notify.ipwl.add.ok', errorKey: 'notify.ipwl.add.fail' },
    mutationFn: (v: { cidr: string; label: string }) =>
      userIpWhitelistApi.add(user.id, v),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-ip-whitelist', user.id] });
      form.reset();
    },
  });

  const removeMut = useMutation({
    meta: { successKey: 'notify.ipwl.remove.ok', errorKey: 'notify.ipwl.remove.fail' },
    mutationFn: (rowId: number) => userIpWhitelistApi.remove(user.id, rowId),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['user-ip-whitelist', user.id] }),
  });

  return (
    <Modal
      opened
      onClose={onClose}
      title={t('users.ipWhitelistTitle', { email: user.email })}
      size="lg"
    >
      <Stack gap="md">
        <Alert color="yellow" icon={<IconAlertCircle size={16} />}>
          {t('users.ipWhitelistDenyAll', { token: 'ip_not_whitelisted' })}
        </Alert>

        <Card withBorder>
          <Text fw={600} mb="sm">{t('users.ipWhitelistAddCidr')}</Text>
          <form onSubmit={form.onSubmit((v) => addMut.mutate(v))}>
            <Group align="end" wrap="nowrap">
              <TextInput
                label={t('users.ipWhitelistCidrLabel')}
                placeholder={t('users.ipWhitelistCidrPlaceholder')}
                style={{ flex: 1 }}
                {...form.getInputProps('cidr')}
              />
              <TextInput
                label={t('users.ipWhitelistLabelLabel')}
                placeholder={t('users.ipWhitelistLabelPlaceholder')}
                style={{ flex: 1 }}
                {...form.getInputProps('label')}
              />
              <Button type="submit" loading={addMut.isPending} leftSection={<IconPlus size={14} />}>
                {t('common.add')}
              </Button>
            </Group>
          </form>
        </Card>

        <Divider label={t('users.ipWhitelistExisting')} labelPosition="left" />

        {listQuery.isLoading && (
          <Center py="md"><Loader size="sm" /></Center>
        )}
        {listQuery.error && (
          <Alert color="red" icon={<IconAlertCircle size={16} />}>
            {listQuery.error instanceof ApiCallError
              ? `${listQuery.error.code}: ${listQuery.error.message}`
              : t('common.error')}
          </Alert>
        )}
        {!listQuery.isLoading && !listQuery.error && (
          <ScrollArea>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('users.ipWhitelistColCidr')}</Table.Th>
                  <Table.Th>{t('users.ipWhitelistColLabel')}</Table.Th>
                  <Table.Th>{t('users.ipWhitelistColEnabled')}</Table.Th>
                  <Table.Th>{t('users.ipWhitelistColCreatedBy')}</Table.Th>
                  <Table.Th>{t('users.ipWhitelistColLastUsed')}</Table.Th>
                  <Table.Th></Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {listQuery.data?.rows.length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={6}>
                      <Text c="dimmed" ta="center" py="lg">
                        {t('users.ipWhitelistEmpty')}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )}
                {listQuery.data?.rows.map((row) => (
                  <Table.Tr key={row.id}>
                    <Table.Td><Code>{row.cidr}</Code></Table.Td>
                    <Table.Td>{row.label}</Table.Td>
                    <Table.Td>
                      <Badge color={row.enabled ? 'green' : 'gray'} variant="light">
                        {row.enabled ? t('users.ipWhitelistEnabledYes') : t('users.ipWhitelistEnabledNo')}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed">{row.createdBy}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed">
                        {row.lastUsedAt ? formatGmt7(row.lastUsedAt) : '—'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Tooltip label={t('users.ipWhitelistRemove')}>
                        <ActionIcon
                          variant="default"
                          color="red"
                          onClick={() => removeMut.mutate(row.id)}
                          loading={removeMut.isPending && removeMut.variables === row.id}
                        >
                          <IconTrash size={14} />
                        </ActionIcon>
                      </Tooltip>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        )}

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>{t('users.ipWhitelistClose')}</Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function CreateUserModal({
  opened,
  scope,
  merchantId,
  onClose,
  onCreated,
}: {
  opened: boolean;
  scope: UserType;
  /** Pre-selected merchantId when the viewer is a MerchantAdmin. null for internal/superuser. */
  merchantId: string | null;
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t } = useTranslation();
  // Check the viewer's identity directly. Relying solely on `merchantId === null`
  // was fragile — a hydrating session that briefly nulled the profile would
  // expose the merchant picker even when the viewer is a MerchantAdmin.
  const { state } = useSession();
  const viewerIsMerchant =
    state.status === 'authenticated' && isMerchantUser(state.profile);

  // Merchant picker shows only when the viewer is internal/superuser AND
  // they're creating an external user. MerchantAdmin viewers always have
  // their own merchantId force-applied by the server.
  const needsMerchantPicker =
    scope === 'external' && !viewerIsMerchant && merchantId === null;

  const rolesQuery = useQuery({
    queryKey: ['roles-for-scope', scope === 'internal' ? 'internal' : 'merchant'],
    queryFn: () => rolesApi.list({ scope: scope === 'internal' ? 'internal' : 'merchant' }),
    enabled: opened,
  });

  // Pull a flat page of merchants for the picker. Up to 100 is fine for
  // dev/small deploys; if the dataset grows past that we'd swap to a
  // server-side typeahead.
  const merchantsQuery = useQuery({
    queryKey: ['merchants-for-user-create'],
    queryFn: () => merchantsApi.list({ limit: 100 }),
    enabled: opened && needsMerchantPicker,
  });

  const form = useForm({
    initialValues: {
      email: '',
      accountName: '',
      password: '',
      merchantId: '' as string,
      merchantRoleId: '' as string,
    },
    validate: {
      email: (v) => (/.+@.+\..+/.test(v) ? null : 'invalid email'),
      accountName: (v) => (v.trim().length === 0 ? 'required' : null),
      password: (v) => (v.length < 12 ? 'min 12 chars' : null),
      merchantId: (v) => (needsMerchantPicker && !v ? 'merchant required for external user' : null),
    },
  });

  const mutation = useMutation({
    meta: { successKey: 'notify.user.create.ok', errorKey: 'notify.user.create.fail' },
    mutationFn: () =>
      usersApi.create({
        email: form.values.email.trim().toLowerCase(),
        accountName: form.values.accountName.trim(),
        password: form.values.password,
        userType: scope,
        // Resolve merchantId precedence:
        //  - MerchantAdmin viewer: use the pre-selected merchantId prop (server
        //    forces it anyway; sending matches helps audit).
        //  - Internal/SuperAdmin viewer creating an external user: use the picker.
        //  - Internal user: null.
        ...(scope === 'external'
          ? { merchantId: merchantId ?? form.values.merchantId }
          : {}),
        merchantRoleId: form.values.merchantRoleId ? Number(form.values.merchantRoleId) : null,
      }),
    onSuccess: () => {
      form.reset();
      onCreated();
    },
  });

  return (
    <Modal opened={opened} onClose={onClose} title={t('users.createTitle', { scope })} size="md">
      <form onSubmit={form.onSubmit(() => mutation.mutate())}>
        <Stack gap="sm">
          <TextInput label={t('users.emailLabel')} required {...form.getInputProps('email')} />
          <TextInput label={t('users.accountNameLabel')} required {...form.getInputProps('accountName')} />
          <PasswordInput
            label={t('users.initialPasswordLabel')}
            description={t('users.initialPasswordDesc')}
            required
            {...form.getInputProps('password')}
          />
          {needsMerchantPicker && (
            <Select
              label={t('users.merchantLabel')}
              description={t('users.merchantDesc')}
              placeholder={merchantsQuery.isLoading ? t('users.merchantPlaceholderLoading') : t('users.merchantPlaceholderSelect')}
              data={(merchantsQuery.data?.rows ?? []).map((m) => ({
                value: m.id,
                label: `${m.name} (${m.email})`,
              }))}
              required
              searchable
              nothingFoundMessage={t('users.merchantNothingFound')}
              {...form.getInputProps('merchantId')}
            />
          )}
          <Select
            label={t('users.roleLabel')}
            description={t('users.roleDesc')}
            data={(rolesQuery.data?.rows ?? []).map((r) => ({
              value: String(r.id),
              label: `${r.name} (${r.slug})`,
            }))}
            clearable
            searchable
            {...form.getInputProps('merchantRoleId')}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose} type="button">{t('common.cancel')}</Button>
            <Button type="submit" loading={mutation.isPending}>{t('common.create')}</Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

function EditUserModal({
  user,
  onClose,
  onSaved,
}: {
  user: UserRow;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const rolesQuery = useQuery({
    queryKey: ['roles-for-scope', user.userType === 'internal' ? 'internal' : 'merchant'],
    queryFn: () => rolesApi.list({ scope: user.userType === 'internal' ? 'internal' : 'merchant' }),
  });
  const form = useForm({
    initialValues: {
      email: user.email,
      accountName: user.accountName,
      password: '',
      merchantRoleId: user.merchantRoleId !== null ? String(user.merchantRoleId) : '',
    },
    validate: {
      email: (v) => (/.+@.+\..+/.test(v.trim()) ? null : 'invalid email'),
      accountName: (v) => (v.trim().length === 0 ? 'required' : null),
      password: (v) => (v.length > 0 && v.length < 12 ? 'min 12 chars' : null),
    },
  });
  const mutation = useMutation({
    meta: { successKey: 'notify.user.update.ok', errorKey: 'notify.user.update.fail' },
    mutationFn: () => {
      const payload: PatchUserInput = {};
      const newEmail = form.values.email.trim().toLowerCase();
      if (newEmail !== user.email) {
        payload.email = newEmail;
      }
      if (form.values.accountName.trim() !== user.accountName) {
        payload.accountName = form.values.accountName.trim();
      }
      if (form.values.password.length > 0) {
        payload.password = form.values.password;
      }
      const desiredRole = form.values.merchantRoleId
        ? Number(form.values.merchantRoleId)
        : null;
      if (desiredRole !== user.merchantRoleId) {
        payload.merchantRoleId = desiredRole;
      }
      return usersApi.patch(user.id, payload);
    },
    onSuccess: onSaved,
  });

  return (
    <Modal opened onClose={onClose} title={t('users.editTitle', { email: user.email })} size="md">
      <form onSubmit={form.onSubmit(() => mutation.mutate())}>
        <Stack gap="sm">
          <TextInput label={t('users.emailLabel')} {...form.getInputProps('email')} />
          <TextInput label={t('users.accountNameLabel')} {...form.getInputProps('accountName')} />
          <PasswordInput
            label={t('users.newPasswordLabel')}
            description={t('users.newPasswordDesc')}
            {...form.getInputProps('password')}
          />
          <Select
            label={t('users.roleLabel')}
            data={(rolesQuery.data?.rows ?? []).map((r: RoleDto) => ({
              value: String(r.id),
              label: `${r.name} (${r.slug})`,
            }))}
            clearable
            searchable
            {...form.getInputProps('merchantRoleId')}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose} type="button">{t('common.cancel')}</Button>
            <Button type="submit" loading={mutation.isPending}>{t('common.save')}</Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

function DeleteUserModal({
  user,
  onClose,
  onDeleted,
}: {
  user: UserRow;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const { t } = useTranslation();
  const mutation = useMutation({
    meta: { successKey: 'notify.user.delete.ok', errorKey: 'notify.user.delete.fail' },
    mutationFn: () => usersApi.remove(user.id),
    onSuccess: onDeleted,
  });
  return (
    <Modal opened onClose={onClose} title={t('users.deleteTitle')} size="sm">
      <Stack gap="sm">
        <Text size="sm">
          {t('users.deleteConfirm', { email: user.email })}
        </Text>
        <Text size="xs" c="dimmed">
          {t('users.deleteWarning')}
        </Text>
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>{t('common.cancel')}</Button>
          <Button color="red" loading={mutation.isPending} onClick={() => mutation.mutate()}>
            {t('common.delete')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
